// SPDX-License-Identifier: Elastic-2.0
/**
 * LightControls — toggle + brightness + (optional) color picker + (optional)
 * color-temperature slider for a `light` device.
 *
 * Per task spec § 2:
 *   - Toggle on/off (`turn_on` / `turn_off`).
 *   - Brightness Slider 0-100, visible only when on
 *     (`set_brightness`, params `{ brightness_pct }`).
 *   - Color picker (native `<input type="color">`), only when
 *     `device.capabilities` includes `"rgb"`
 *     (`set_color`, params `{ hex }`).
 *   - Color-temp slider, only when `capabilities` includes `"color_temp"`
 *     (`set_color_temp`, params `{ mireds }`). 153-500 mired is the HA-wide
 *     useful range; we use that here for v0.1.
 *
 * Capability detection note: F-300 `Device` does NOT yet declare a typed
 * `capabilities` field — it ships under `state.capabilities` (array of
 * strings) per the worked examples in the functional spec. We read it
 * defensively and never throw on shape drift.
 */
import { Slider } from "@/components/Slider";
import { Toggle } from "@/components/Toggle";

import type { Device } from "@/api/gateway";

import styles from "../DeviceModal.module.css";

export interface LightControlsProps {
  device: Device;
  onAction: (
    action: string,
    params: Record<string, unknown>,
    optimisticStatePatch?: Record<string, unknown>,
  ) => void;
  disabled?: boolean;
}

/** Defensive capability read — handles missing field, wrong shape, etc. */
function getCapabilities(device: Device): readonly string[] {
  const fromState = (device.state as { capabilities?: unknown })?.capabilities;
  if (Array.isArray(fromState)) {
    return fromState.filter((c): c is string => typeof c === "string");
  }
  return [];
}

/** Convert a 0-255 RGB triple from `state.color` to `"#RRGGBB"`. */
function rgbToHex(color: unknown): string | null {
  if (!Array.isArray(color) || color.length < 3) return null;
  const [r, g, b] = color;
  if (typeof r !== "number" || typeof g !== "number" || typeof b !== "number") {
    return null;
  }
  const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return (
    "#" +
    [r, g, b]
      .map(clamp)
      .map((n) => n.toString(16).padStart(2, "0"))
      .join("")
  );
}

export function LightControls({
  device,
  onAction,
  disabled,
}: LightControlsProps): JSX.Element {
  const state = device.state ?? {};
  const on = Boolean(state.on);
  const brightness = typeof state.brightness === "number" ? state.brightness : 100;
  const capabilities = getCapabilities(device);
  const supportsRgb = capabilities.includes("rgb");
  const supportsColorTemp = capabilities.includes("color_temp");
  const currentColor = rgbToHex(state.color) ?? "#ffffff";
  const currentMireds = typeof state.color_temp === "number" ? state.color_temp : 250;

  function handleToggle(next: boolean): void {
    onAction(next ? "turn_on" : "turn_off", {}, { on: next });
  }

  function handleBrightness(value: number): void {
    onAction(
      "set_brightness",
      { brightness_pct: value },
      { brightness: value, on: value > 0 },
    );
  }

  function handleColor(event: React.ChangeEvent<HTMLInputElement>): void {
    onAction("set_color", { hex: event.target.value });
  }

  function handleColorTemp(value: number): void {
    onAction("set_color_temp", { mireds: value }, { color_temp: value });
  }

  return (
    <div className={styles.controlsStack} data-testid="light-controls">
      <Toggle
        checked={on}
        onChange={handleToggle}
        label={device.name}
        disabled={disabled}
        aria-label={`Toggle ${device.name}`}
      />
      {on ? (
        <div data-testid="light-brightness">
          <Slider
            value={brightness}
            onChange={handleBrightness}
            min={0}
            max={100}
            label="Brightness"
            formatValue={(v) => `${v}%`}
            disabled={disabled}
          />
        </div>
      ) : null}
      {on && supportsRgb ? (
        <div className={styles.colorRow} data-testid="light-color">
          <label className={styles.fieldLabel} htmlFor={`color-${device.id}`}>
            Color
          </label>
          <input
            id={`color-${device.id}`}
            type="color"
            value={currentColor}
            onChange={handleColor}
            disabled={disabled}
            aria-label="Color"
          />
        </div>
      ) : null}
      {on && supportsColorTemp ? (
        <div data-testid="light-color-temp">
          <Slider
            value={currentMireds}
            onChange={handleColorTemp}
            min={153}
            max={500}
            label="Color temperature"
            formatValue={(v) => `${v} mired`}
            disabled={disabled}
          />
        </div>
      ) : null}
    </div>
  );
}

export default LightControls;
